import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Lock,
  Shield,
  Key,
  User,
  Save,
  RotateCcw,
  CheckCircle2,
  Eye,
  Settings,
  HelpCircle,
  ShieldCheck,
  ArrowRight,
  Info,
} from 'lucide-react';

export default function LoginCustomizerManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  const defaultLoginConfig = {
    badgeText: 'Portal Khusus Tim & Redaksi',
    title: 'Login Dasbor Redaksi',
    subtitle: 'Masuk untuk mengelola artikel, marketplace UMKM, portofolio, dan tema website',
    usernameLabel: 'Username / ID Pengguna',
    usernamePlaceholder: 'Masukkan username Anda',
    passwordLabel: 'Kata Sandi (Password)',
    passwordPlaceholder: '••••••••',
    submitButtonText: 'Masuk ke Dasbor',
    backHomeText: '← Kembali ke Halaman Depan',
    footerNotice: 'Sistem terenkripsi dengan proteksi Anti Brute-Force aktif',
    showSecretPathHint: true,
    customIcon: 'lock' as 'lock' | 'shield' | 'key' | 'user',
  };

  const [loginConfig, setLoginConfig] = useState({
    ...defaultLoginConfig,
    ...(settings?.loginPageConfig || {}),
  });

  const [saving, setSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);
  const primaryColor = settings?.theme.primaryColor || '#059669';

  const handleSave = async () => {
    setSaving(true);
    const success = await updateSettingsOnServer({
      loginPageConfig: loginConfig,
    });
    setSaving(false);
    if (success) {
      const timeStr = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLastSavedTime(timeStr);
      showToast('Kustomisasi Halaman Login berhasil disimpan!', 'success');
    }
  };

  const handleReset = () => {
    if (window.confirm('Kembalikan semua teks dan opsi halaman login ke pengaturan standar?')) {
      setLoginConfig(defaultLoginConfig);
      showToast('Pengaturan di-reset ke default. Klik Simpan untuk menerapkan.', 'info');
    }
  };

  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'shield':
        return <Shield className="w-5 h-5" />;
      case 'key':
        return <Key className="w-5 h-5" />;
      case 'user':
        return <User className="w-5 h-5" />;
      case 'lock':
      default:
        return <Lock className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-slate-800 tracking-tight">Kustomisasi Halaman Login</h2>
            <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
              Desain & Redaksi
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Ubah semua kata-kata, label input, placeholder, tombol, ikon, dan opsi visibilitas pada halaman login tim & redaksi.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              type="button"
              className="px-3.5 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset Default</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              type="button"
              className="px-5 py-2 text-xs font-bold text-white rounded-xl shadow-md hover:opacity-95 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Simpan Kustomisasi</span>
                </>
              )}
            </button>
          </div>

          {lastSavedTime && (
            <div
              id="login-customizer-saved-notice"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs font-bold animate-in fade-in slide-in-from-top-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
              <span>Kustomisasi login berhasil disimpan</span>
              <span className="text-[10px] text-emerald-700/80 font-mono font-medium">({lastSavedTime})</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Form: Settings Inputs */}
        <div className="lg:col-span-7 space-y-6">
          {/* Card 1: Judul, Subjudul & Badge */}
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-2 text-xs font-black text-slate-800 uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4 text-amber-500" />
              <span>Header & Branding Teks Login</span>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                Badge / Tagline Atas (Opsional)
              </label>
              <input
                type="text"
                value={loginConfig.badgeText || ''}
                onChange={(e) => setLoginConfig({ ...loginConfig, badgeText: e.target.value })}
                placeholder="Contoh: Portal Khusus Tim & Redaksi"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
              />
              <p className="text-[11px] text-slate-400 mt-1">Muncul sebagai label kecil di atas judul login.</p>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                Judul Halaman Login (Title)
              </label>
              <input
                type="text"
                value={loginConfig.title || ''}
                onChange={(e) => setLoginConfig({ ...loginConfig, title: e.target.value })}
                placeholder="Contoh: Login Dasbor Redaksi"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800 font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                Subjudul / Deskripsi Pendukung
              </label>
              <textarea
                rows={2}
                value={loginConfig.subtitle || ''}
                onChange={(e) => setLoginConfig({ ...loginConfig, subtitle: e.target.value })}
                placeholder="Contoh: Masuk untuk mengelola artikel, marketplace UMKM, portofolio..."
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">
                Pilihan Ikon Utama Login
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: 'lock', label: 'Gembok (Lock)' },
                  { id: 'shield', label: 'Perisai (Shield)' },
                  { id: 'key', label: 'Kunci (Key)' },
                  { id: 'user', label: 'Pengguna (User)' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setLoginConfig({ ...loginConfig, customIcon: item.id as any })}
                    className={`p-3 rounded-xl border flex flex-col items-center gap-1.5 text-center transition-all cursor-pointer ${
                      loginConfig.customIcon === item.id
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-800 font-bold shadow-xs'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: loginConfig.customIcon === item.id ? primaryColor : '#94A3B8' }}
                    >
                      {renderIcon(item.id)}
                    </div>
                    <span className="text-[10px]">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Card 2: Form Fields & Tombol */}
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-2 text-xs font-black text-slate-800 uppercase tracking-wider">
              <Settings className="w-4 h-4 text-blue-500" />
              <span>Label Form & Teks Tombol</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Label Input Username
                </label>
                <input
                  type="text"
                  value={loginConfig.usernameLabel || ''}
                  onChange={(e) => setLoginConfig({ ...loginConfig, usernameLabel: e.target.value })}
                  placeholder="Username / ID Pengguna"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Placeholder Username
                </label>
                <input
                  type="text"
                  value={loginConfig.usernamePlaceholder || ''}
                  onChange={(e) => setLoginConfig({ ...loginConfig, usernamePlaceholder: e.target.value })}
                  placeholder="Masukkan username Anda"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Label Input Password
                </label>
                <input
                  type="text"
                  value={loginConfig.passwordLabel || ''}
                  onChange={(e) => setLoginConfig({ ...loginConfig, passwordLabel: e.target.value })}
                  placeholder="Kata Sandi (Password)"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Placeholder Password
                </label>
                <input
                  type="text"
                  value={loginConfig.passwordPlaceholder || ''}
                  onChange={(e) => setLoginConfig({ ...loginConfig, passwordPlaceholder: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Teks Tombol Submit (Masuk)
                </label>
                <input
                  type="text"
                  value={loginConfig.submitButtonText || ''}
                  onChange={(e) => setLoginConfig({ ...loginConfig, submitButtonText: e.target.value })}
                  placeholder="Masuk ke Dasbor"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800 font-bold"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Teks Tautan Kembali ke Beranda
                </label>
                <input
                  type="text"
                  value={loginConfig.backHomeText || ''}
                  onChange={(e) => setLoginConfig({ ...loginConfig, backHomeText: e.target.value })}
                  placeholder="← Kembali ke Halaman Depan"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                Catatan Footer Keamanan (Bawah Form)
              </label>
              <input
                type="text"
                value={loginConfig.footerNotice || ''}
                onChange={(e) => setLoginConfig({ ...loginConfig, footerNotice: e.target.value })}
                placeholder="Sistem terenkripsi dengan proteksi Anti Brute-Force aktif"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
              />
            </div>
          </div>

          {/* Card 3: Opsi Tambahan & Visibilitas */}
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-2 text-xs font-black text-slate-800 uppercase tracking-wider">
              <Eye className="w-4 h-4 text-purple-500" />
              <span>Opsi & Keamanan Tampilan</span>
            </div>

            <div className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200">
              <div className="space-y-0.5 pr-4">
                <span className="text-xs font-bold text-slate-800 block">
                  Tampilkan Petunjuk URL Rahasia (Bila Dikonfigurasi)
                </span>
                <p className="text-[11px] text-slate-500">
                  Jika dinonaktifkan, kotak peringatan jalur rahasia tidak akan ditampilkan di halaman login demi kerahasiaan maksimal.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={loginConfig.showSecretPathHint !== false}
                  onChange={(e) =>
                    setLoginConfig({ ...loginConfig, showSecretPathHint: e.target.checked })
                  }
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-slate-300 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Right Column: Live Interactive Preview */}
        <div className="lg:col-span-5 space-y-4">
          <div className="sticky top-20 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-emerald-600" />
                <span>Pratinjau Langsung (Live Preview)</span>
              </span>
              <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-bold">
                Tampilan Pengguna
              </span>
            </div>

            {/* Mock Login Page Container */}
            <div className="p-6 bg-slate-100 rounded-3xl border border-slate-300 shadow-inner">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg space-y-5 text-left">
                {/* Brand & Icon */}
                <div className="text-center">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white mx-auto shadow-md mb-3"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {renderIcon(loginConfig.customIcon || 'lock')}
                  </div>

                  {loginConfig.badgeText && (
                    <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-bold rounded-full mb-1.5">
                      {loginConfig.badgeText}
                    </span>
                  )}

                  <h3 className="text-lg font-extrabold text-slate-900 leading-tight">
                    {loginConfig.title || 'Login Dasbor Redaksi'}
                  </h3>
                  {loginConfig.subtitle && (
                    <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                      {loginConfig.subtitle}
                    </p>
                  )}
                </div>

                {/* Secret Notice Preview */}
                {loginConfig.showSecretPathHint !== false && settings?.security?.customLoginPath && (
                  <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-[11px] text-amber-800 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span>Jalur rahasia: /{settings.security.customLoginPath.replace(/^\/+/, '')}</span>
                  </div>
                )}

                {/* Form fields */}
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block mb-1">
                      {loginConfig.usernameLabel || 'Username / ID Pengguna'}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        disabled
                        placeholder={loginConfig.usernamePlaceholder || 'Masukkan username Anda'}
                        className="w-full pl-8 pr-3 py-2 text-[11px] bg-slate-50 border border-slate-200 rounded-lg text-slate-500 cursor-not-allowed"
                      />
                      <User className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block mb-1">
                      {loginConfig.passwordLabel || 'Kata Sandi (Password)'}
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        disabled
                        placeholder={loginConfig.passwordPlaceholder || '••••••••'}
                        className="w-full pl-8 pr-3 py-2 text-[11px] bg-slate-50 border border-slate-200 rounded-lg text-slate-500 cursor-not-allowed"
                      />
                      <Lock className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled
                    className="w-full py-2.5 rounded-lg text-white font-bold text-xs shadow-md flex items-center justify-center gap-1.5 cursor-not-allowed"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <span>{loginConfig.submitButtonText || 'Masuk ke Dasbor'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Footer Notice */}
                {loginConfig.footerNotice && (
                  <p className="text-[10px] text-center text-slate-400 flex items-center justify-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    <span>{loginConfig.footerNotice}</span>
                  </p>
                )}

                {/* Back button preview */}
                <div className="text-center pt-1 border-t border-slate-100">
                  <span className="text-[11px] font-medium text-slate-500">
                    {loginConfig.backHomeText || '← Kembali ke Halaman Depan'}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-emerald-50 rounded-2xl border border-emerald-200 text-xs text-emerald-800 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Kredensial Login Aman</span>
              </div>
              <p className="text-[11px] text-emerald-700 leading-relaxed">
                Halaman login tidak lagi mengekspos username dan password default secara publik. Setiap akun memiliki kredensial pribadi yang dapat dikelola di tab <strong>Manajemen Pengguna & Peran</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
